import { ReactNode } from "react";
import cx from "classnames";

import { useTransition } from "common/hooks";
import { stopPropagation } from "common/utils";
import { StackableProps } from "common/stackable/types";

import { useBreakpoints } from "client/common/hooks";

import Overlay from "./Overlay";

import styles from "../../styles/Drawer.module.less";

export type DrawerProps = {
  children: ReactNode;
  /**
   * Lifts the drawer above `presentation`. A drawer opened over a fullscreen container ties it
   * on z-index and loses on DOM order.
   */
  isAbovePresentation?: boolean;
} & StackableProps;

const Drawer = ({
  isFocused,
  stackIndex,
  onClose,
  children,
  isAbovePresentation = false,
}: DrawerProps) => {
  const isVisible = true; // If this component is mounted, it is visible
  const { isTabletPortraitAndSmaller } = useBreakpoints();
  const transition = useTransition(isVisible);
  const { transitionState, durationStyle } = transition;

  const stackablePadding = isTabletPortraitAndSmaller
    ? { paddingTop: Math.min(stackIndex, 2) * 15 }
    : { paddingLeft: Math.min(stackIndex, 2) * 20 };

  return (
    <Overlay
      transition={transition}
      isVisible={stackIndex === 0}
      isFocused={isFocused}
      onClose={onClose}
      className={cx(isAbovePresentation && styles.abovePresentation)}
    >
      <div className={cx(styles.layout, isTabletPortraitAndSmaller && styles.compact)}>
        <div style={stackablePadding} className={styles.availableSpace}>
          <div
            className={cx(styles.container, styles[`transition-${transitionState}`])}
            style={{ transitionDuration: durationStyle }}
            onMouseDown={stopPropagation}
            onClick={stopPropagation}
            role="presentation"
          >
            <div className={styles.content}>{children}</div>
          </div>
        </div>
      </div>
    </Overlay>
  );
};

export default Drawer;
